<script setup lang="ts">
import { AppTextInput, AppTypography } from '@/components/base';
import { AppLogo, LocationPickerModal } from '@/components/layout';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLayoutStore } from '~/stores/layout';
import { useLocationStore } from '~/stores/location';
import {
  REPORT_ENTITY_SECTIONS,
  SUPPORT_SECTION_PATHS,
  TOPBAR_NAV,
} from '~/utils/enums/navigation';

const layout = useLayoutStore();
const location = useLocationStore();
const router = useRouter();

const search = ref('');
const modalVisible = ref(false);

function onSearchEnter() {
  const query = search.value.trim();
  // Clearing the field while already viewing results must still navigate,
  // otherwise route.query.q never changes and the stale results card stays
  // on screen (the search page only reacts to its own query param).
  if (!query) {
    if (route?.path === '/search') router.push({ path: '/search' });
    return;
  }
  router.push({ path: '/search', query: { q: query } });
}

// Mobile: the bar is transparent only while the page sits at the very top;
// as soon as the user scrolls it turns solid so content doesn't collide
// with the nav. (Desktop is always solid — the class only matters <lg.)
const scrolled = ref(false);
const onScroll = () => {
  scrolled.value = window.scrollY > 8;
};

onMounted(() => {
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});

// Mobile header treatment (per Figma): Home keeps the full logo lockup; every
// other root subpage (Candidates, Parties, Campaigns, Sources, About,
// Search) pairs the small square mark with its title; detail pages swap the
// mark for a back arrow + title.
const route = useRoute();

// The bar is persistent, so the field would otherwise keep whatever was last
// typed regardless of where the user is. Mirror the search page's ?q= instead:
// the term stays visible (and editable) for as long as they're looking at its
// results — including on reload or a shared link — and clears on the way out.
watch(
  () => (route?.path === '/search' ? (route?.query.q ?? '') : ''),
  (value) => {
    const next = typeof value === 'string' ? value : '';
    if (next !== search.value) search.value = next;
  },
  { immediate: true },
);

// AppTopbar lives in the persistent layout, so it never remounts between
// pages — `onScroll` only ran once at initial mount. Without this, scrolling
// down on one page then navigating to another (client-side) could leave
// `scrolled` stuck `true` from the previous page, showing the solid
// background even though the new page starts at the very top.
//
// A fresh navigation always lands at the top of the new page, but Nuxt's
// scroll-to-top reset happens asynchronously (nextTick) — after this
// pre-flush watcher runs — so reading `window.scrollY` here could still see
// the previous page's offset. Reset optimistically instead; the existing
// scroll listener re-solidifies the bar as soon as the user actually
// scrolls on the new page.
watch(
  () => route?.path,
  () => {
    scrolled.value = false;
  },
);

// Desktop nav highlight: NuxtLink's default `router-link-active` class is
// based on matched route records, not URL prefix. Detail routes like
// `/parties/[id]` are sibling pages to `/parties/index`, not nested under
// it, so the router never considers `/parties/some-slug` a match for the
// `/parties` link and the tab loses its highlight on detail pages. Compute
// active state from the path instead so section detail pages keep their
// parent tab highlighted.
function isNavItemActive(to: string) {
  const path = route?.path ?? '/';
  if (to === '/') return path === '/';
  if (to === '/support' && SUPPORT_SECTION_PATHS.includes(path)) return true;
  return path === to || path.startsWith(`${to}/`);
}

const MOBILE_ROOT_TITLES: Record<string, string> = {
  '/candidates': 'Candidates',
  '/parties': 'Parties',
  '/campaigns': 'Campaigns',
  '/sources': 'Sources',
  '/about': 'About',
  '/support': 'Help Centre',
  '/search': 'Search',
  '/report-a-problem': 'Report a Problem',
};

// Terms of Use / Privacy Policy / Report a Problem (per Figma): mobile shows
// a bare back arrow only — no logo, no title text, no hamburger — unlike
// other detail pages which pair the back arrow with a centered title.
const BACK_ONLY_MOBILE_PATHS = new Set([
  '/terms-of-use',
  '/privacy-policy',
  '/report-a-problem',
]);

const mobileHeader = computed<{
  title?: string;
  back?: string;
  hideBurger?: boolean;
} | null>(() => {
  // `useRoute()` is undefined when mounted without a router (unit tests);
  // fall back to the Home treatment (logo) in that case.
  const p = route?.path ?? '/';
  if (p === '/') return null;
  if (/^\/candidates\/[^/]+$/.test(p))
    return { title: 'Candidate Profile', back: '/candidates' };
  if (/^\/parties\/[^/]+$/.test(p))
    return { title: 'Party Profile', back: '/parties' };
  if (/^\/campaigns\/[^/]+$/.test(p))
    return { title: 'Campaign Details', back: '/campaigns' };
  if (BACK_ONLY_MOBILE_PATHS.has(p)) {
    // /report-a-problem reached via an entity's "Help us improve this
    // information" link should return to that entity's own Details page
    // (e.g. /campaigns/some-campaign) rather than Support or even just the
    // section list — the reporter never came from the Help Centre in that
    // flow, and landed on this page from a specific candidate/party/campaign,
    // not the section as a whole (VTW-495). This only matters for the no-history
    // fallback below (`onBack` normally pops real history straight back to
    // that page); it's the deep-link case that needs this specific target.
    const reportType = route?.query.report_type;
    const reportId = route?.query.report_id;
    const section =
      typeof reportType === 'string'
        ? REPORT_ENTITY_SECTIONS[reportType]
        : null;
    const back =
      section && typeof reportId === 'string'
        ? `${section.to}/${reportId}`
        : (section?.to ?? '/support');
    return { back, hideBurger: true };
  }
  const title = MOBILE_ROOT_TITLES[p];
  if (!title) return null;
  return { title };
});

// Native-app back: pop real history whenever a previous entry exists
// (preserves scroll/filters on the previous screen, mirrors the platform
// back gesture); deep links with no history at all fall back to the
// section list route.
//
// `window.history.state?.back` looks like the natural check here, but it
// lies after a hard navigation: some links (e.g. CampaignMiniCard on
// mobile) are plain <a> tags that do a full page load rather than an SPA
// push, and Vue Router re-initialises its history state on that load with
// `back: null` — even though the browser's real history stack still has
// the previous page. `history.length > 1` survives that case since it
// reflects the browser's actual stack, not Vue Router's.
function onBack() {
  if (window.history.length > 1) {
    router.back();
    return;
  }
  const fallback = mobileHeader.value?.back;
  if (fallback) router.push(fallback);
}
</script>

<template>
  <header
    class="app-topbar fixed top-0 right-0 left-0 z-1000 flex"
    :class="{ 'app-topbar--scrolled': scrolled }"
    role="banner"
  >
    <div
      class="app-topbar__border layout-size flex items-center justify-between gap-2 min-[1340px]:gap-4"
    >
      <div class="flex min-w-0 shrink-0 items-center gap-8 lg:gap-[72px]">
        <AppLogo data-testid="logo" class="hidden lg:block" />
        <!-- Mobile: Home shows the full logo; list/root subpages show the
             small square mark + title so there's still a way back to Home
             without opening the hamburger menu; detail pages add a back
             arrow at the left instead (Figma). -->
        <div
          data-testid="mobile-header"
          class="flex min-w-0 items-center gap-2 lg:hidden"
        >
          <AppLogo
            v-if="!mobileHeader"
            src="/logo-mobile.svg"
            width="129px"
            height="24px"
          />
          <template v-else>
            <button
              v-if="mobileHeader.back"
              type="button"
              aria-label="Back"
              class="app-topbar__back flex items-center"
              @click="onBack"
            >
              <Icon name="solar:arrow-left-linear" class="text-xl" />
            </button>
            <AppLogo v-else src="/logo-square.svg" width="24px" height="24px" />
            <!-- Centered only on detail pages (those with a back button);
                 list/root pages keep the title at the left (Figma). Back-only
                 pages (Terms of Use / Privacy Policy) render no title at all. -->
            <AppTypography
              v-if="mobileHeader.title"
              type="title-6"
              :class="
                mobileHeader.back ? 'app-topbar__mobile-title' : 'truncate'
              "
            >
              {{ mobileHeader.title }}
            </AppTypography>
          </template>
        </div>
        <nav class="hidden items-center gap-2 min-[1340px]:gap-6 lg:flex">
          <NuxtLink
            v-for="item in TOPBAR_NAV"
            :key="item.to"
            :to="item.to"
            class="app-topbar__link px-1 py-2 no-underline min-[1340px]:px-2"
            :class="{ 'app-topbar__link--active': isNavItemActive(item.to) }"
          >
            <AppTypography
              type="subheadline-bold"
              class="app-topbar__link-label"
            >
              {{ item.label }}
            </AppTypography>
          </NuxtLink>
        </nav>
      </div>

      <div class="flex items-center gap-2 min-[1340px]:gap-4">
        <div
          class="app-topbar__search hidden min-w-0 items-center lg:flex"
          :class="
            location.isSet
              ? 'app-topbar__search--merged'
              : 'app-topbar__search--paired'
          "
        >
          <span class="app-topbar__icon-frame shrink-0">
            <Icon
              name="solar:magnifer-linear"
              class="app-topbar__search-icon"
            />
          </span>
          <AppTextInput
            v-model="search"
            placeholder="Search VoteWise"
            :floating-label="false"
            variant="outlined"
            size="small"
            enterkeyhint="search"
            class="app-topbar__search-input min-w-0 flex-1"
            @keyup.enter="onSearchEnter"
          />

          <!-- Once a location is set, its chip moves inside the search field
               (Figma), matching the candidates list's mobile search chip. -->
          <button
            v-if="location.isSet"
            data-testid="location-chip"
            type="button"
            class="app-topbar__search-location flex shrink-0 items-center gap-1"
            @click="modalVisible = true"
          >
            <Icon
              name="solar:map-point-linear"
              class="app-topbar__search-location-icon shrink-0"
            />
            <AppTypography type="caption" class="truncate">
              {{ location.displayLabel }}
            </AppTypography>
          </button>
        </div>

        <button
          v-if="!location.isSet"
          data-testid="location-chip"
          type="button"
          class="app-topbar__chip hidden shrink-0 items-center lg:flex"
          @click="modalVisible = true"
        >
          <span class="app-topbar__icon-frame shrink-0">
            <Icon name="solar:map-point-linear" class="app-topbar__chip-icon" />
          </span>
          <AppTypography type="subheadline" class="app-topbar__chip-label"
            >Choose Your Location</AppTypography
          >
        </button>
        <button
          v-if="!mobileHeader?.hideBurger"
          type="button"
          class="app-topbar__burger lg:hidden"
          aria-label="Open menu"
          @click="layout.onMenuToggle()"
        >
          <Icon name="solar:hamburger-menu-linear" class="text-2xl" />
        </button>
      </div>
    </div>

    <LocationPickerModal
      v-if="modalVisible"
      v-model:visible="modalVisible"
      data-testid="location-modal"
    />
  </header>
</template>

<style lang="scss" scoped>
// When a modal locks body scroll, PrimeVue hides the page scrollbar and
// compensates the BODY with `padding-right: var(--p-scrollbar-width)` under
// `.p-overflow-hidden` — but this bar is `position: fixed; left-0 right-0`,
// so it spans the now-wider viewport uncompensated and its content visibly
// shifts right on every modal open. Mirror the body's own compensation so
// the bar's content stays put; the extra strip of bar background where the
// scrollbar was sits under the modal mask anyway.
//
// Whole-selector :global at top level on purpose: nesting `:global(...) &`
// inside `.app-topbar` compiles to a rule that pads the BODY, not this bar.
:global(body.p-overflow-hidden .app-topbar) {
  padding-right: var(--p-scrollbar-width, 0px);
}

.app-topbar {
  height: var(--header-height);
  border-bottom: 1px solid $border-color;
  background-color: $surface-color-bg;

  // Mobile (per Figma): the nav is transparent — just logo + burger floating
  // over the sand/map hero, no bar or border — but ONLY while at the top of
  // the page. Once scrolled it turns solid (see &--scrolled). Desktop keeps
  // the white bar throughout.
  @media (max-width: 1023px) {
    border-bottom: none;
    background-color: transparent;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;
  }

  &--scrolled {
    @media (max-width: 1023px) {
      border-bottom: 1px solid $border-color;
      background-color: $surface-color-bg;
    }
  }

  // Active nav item: the underline is the gold from the Figma palette (the
  // Figma component is named "Tab - Line" and this instance renders every tab
  // unselected, so the underline is retained as the selected-state
  // affordance, recoloured to the new palette), not the global router-link
  // white background.
  &__link {
    padding-bottom: 2px;
    border-bottom: 4px solid transparent;

    &.router-link-active {
      background: transparent;
    }

    &.app-topbar__link--active {
      border-bottom-color: $brand-color;

      :deep(*) {
        color: $text-color-brand;
      }
    }
  }

  &__link-label {
    color: $complementary-color-darker-shade-1;
  }

  // Search (Figma "Search Field"): a fixed 384px rounded rectangle — radius/sm,
  // not a pill — holding the magnifier, a borderless input, and the location
  // chip tucked against the right edge.
  // AppTextInput applies its `class`/attrs onto the inner `.p-inputtext`
  // element itself (via inheritAttrs:false + v-bind="$attrs"), so the inner
  // input is styled here through the `__search` wrapper with :deep().
  &__search {
    height: 40px;
    gap: 8px;
    border: 1px solid #e8e3db; // Figma: borders/default (warm)
    border-radius: 8px; // Figma: radius/sm
    padding: 8px;
    background-color: $surface-color-bg;

    // Below 1340px there isn't room for the fixed 456px total alongside the
    // nav (VTW-518) — let the field size to its content instead of
    // overflowing; the fixed, width-matched sizing below only applies once
    // there's actually space for it.
    width: auto;

    // Merged: the location chip renders inside this field, so the field is
    // the whole widget — 456px (Figma) once there's room.
    &--merged {
      @media (min-width: 1340px) {
        width: 456px;
      }
    }

    // Paired: the location chip is the sibling &__chip pill instead, offset
    // by its ml-4 gap — sized so field + gap + chip still total 456px, the
    // same width as the merged state above.
    &--paired {
      @media (min-width: 1340px) {
        width: 240px; // 240 + 16 (ml-4) + 200 (&__chip) = 456
      }
    }

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.p-inputtext) {
      width: 100%;
      // 24px content + 8px padding top and bottom fills the 40px field. At the
      // old 32px the content overflowed and the vertical padding collapsed.
      height: 24px;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;
      // Figma: Body/Regular • 16 in text/primary (#201e1a). The design shows
      // the prompt at full strength rather than a dimmed placeholder.
      font-size: 16px;
      line-height: 16px;
      color: #201e1a;

      &::placeholder {
        color: #201e1a;
      }

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }
    }
  }

  &__icon-frame {
    @include icon-frame-sm;
  }

  &__search-icon {
    color: #201e1a; // Figma: text/primary
  }

  // Figma: fields-tags surface — light chip, distinct from the darker
  // &__chip pill shown before a location is set.
  &__search-location {
    gap: 4px;
    max-width: 118px;
    padding: 4px 10px;
    border: 1px solid $surface-color-disabled;
    border-radius: 9999px;
    background-color: $surface-color-tag;
    color: $text-color-subtext;
    cursor: pointer;
  }

  &__search-location-icon {
    color: $text-color-tag;
  }

  // Location chip: sits inside the search field, against its right edge.
  // Figma "Chips": a filled pill sitting beside the search field, not inside
  // it. Fixed 200px in the design; max-width lets a long suburb label ellipsize
  // instead of pushing the nav around once a location is set.
  &__chip {
    gap: 0;
    // Same tablet-tier narrowing as &__search above (VTW-518).
    min-width: 130px;
    max-width: 130px;
    height: 40px;

    @media (min-width: 1340px) {
      min-width: 200px;
      max-width: 200px;
    }
    padding: 4px 12px 4px 8px; // Figma: py xs, pl sm, pr md
    border: none;
    border-radius: 64px; // Figma: radius/6xl
    background-color: $complementary-color-darker-shade-1;
    color: $text-color-inverted;
    cursor: pointer;

    &.router-link-active {
      background-color: $complementary-color-darker-shade-1;
    }
  }

  &__chip-icon {
    flex-shrink: 0;
  }

  // Long suburb labels are truncated rather than blowing out the fixed-width
  // search field (the Figma shows "Albert Park...").
  &__chip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__back {
    color: $text-color-primary;

    &.router-link-active,
    &.router-link-exact-active {
      background: transparent;
    }
  }

  // Mobile page title: centered in the bar regardless of what sits at the
  // edges (back arrow / burger), per the Figma.
  &__mobile-title {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-width: 60vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
